import React, { useEffect, useState } from "react";
import { FaBrain, FaPersonRunning } from "react-icons/fa6";
import { FiCloud, FiFileText, FiHeart, FiMessageCircle, FiMusic } from "react-icons/fi";
import SteffiDevice from "../components/SteffiDevice";
import PhoneMock, { useMockScreens } from "../components/PhoneMock";
import DeviceScreenFrame from "../components/DeviceScreenFrame";
import Reveal, { useInView, useTilt } from "../components/Reveal";
import { APP_SHOTS, useLocalizedAsset } from "../renders";
import { useSteffiCopy } from "../content";

/* Where each suggestion floats around her, in polar coordinates.
   Angles are measured clockwise from three o'clock, y pointing down. */
const ORBIT = [
  { Icon: FaBrain, angle: -152, radius: 49 },
  { Icon: FaPersonRunning, angle: -124, radius: 49 },
  { Icon: FiMusic, angle: -55, radius: 49 },
  { Icon: FiMessageCircle, angle: -8, radius: 49 },
  { Icon: FiHeart, angle: 42, radius: 49 },
  { Icon: FiFileText, angle: 128, radius: 49 },
  { Icon: FiCloud, angle: 176, radius: 49 },
];

const ACTIVITY_SCREENS = [
  {
    src: "/steffi/screens/activity-game.webp",
    width: 977,
    height: 977,
    filter: "brightness(1.03) contrast(1.01)",
  },
  {
    src: "/steffi/screens/activity-wordle.webp",
    width: 999,
    height: 999,
    filter: "brightness(1.03) contrast(1.01)",
  },
  {
    src: "/steffi/screens/activity-quiz.webp",
    width: 1080,
    height: 1016,
    filter: "brightness(1.08) contrast(1.03) saturate(.98)",
  },
  {
    src: "/steffi/screens/weather.webp",
    width: 1001,
    height: 1001,
    filter: "brightness(1.03) contrast(1.01)",
  },
  {
    src: "/steffi/screens/activity-mindfulness.webp",
    width: 1004,
    height: 1004,
    filter: "brightness(1.03) contrast(1.01)",
  },
  {
    src: "/steffi/screens/activity-crossword.webp",
    width: 1005,
    height: 1005,
    filter: "brightness(1.03) contrast(1.01)",
  },
  {
    src: "/steffi/screens/activity-flags.webp",
    width: 1005,
    height: 1005,
    filter: "brightness(1.02) contrast(1.01)",
  },
  {
    src: "/steffi/screens/activity-map.webp",
    width: 988,
    height: 988,
    filter: "brightness(1.02) contrast(1.01)",
  },
  {
    src: "/steffi/screens/wellbeing-face.webp",
    width: 714,
    height: 714,
    filter: "none",
  },
  {
    src: "/steffi/screens/music-left.webp",
    width: 584,
    height: 584,
    filter: "none",
  },
  {
    src: "/steffi/screens/music-right.webp",
    width: 554,
    height: 554,
    filter: "none",
  },
];

function ActivityGameScreen({ active }) {
  const localize = useLocalizedAsset();
  const [screenIndex, setScreenIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setScreenIndex(0);
      return undefined;
    }
    const id = setInterval(
      () => setScreenIndex((index) => (index + 1) % ACTIVITY_SCREENS.length),
      4200
    );
    return () => clearInterval(id);
  }, [active]);

  return (
    <DeviceScreenFrame>
      <>
        {ACTIVITY_SCREENS.map((screen, index) => (
          <div
            key={screen.src}
            aria-hidden={screenIndex !== index}
            className="absolute inset-0 overflow-hidden rounded-full"
            style={{
              opacity: screenIndex === index ? 1 : 0,
              transform: `scale(${screenIndex === index ? 1 : 1.025})`,
              transition:
                "opacity 900ms cubic-bezier(.2,.8,.2,1), transform 1100ms cubic-bezier(.2,.8,.2,1)",
            }}
          >
            <img
              src={localize(screen.src)}
              width={screen.width}
              height={screen.height}
              alt=""
              className="absolute inset-0 h-full w-full max-w-none object-cover"
              style={{ filter: screen.filter }}
            />
            {screen.lightMask && (
              <img
                src={screen.lightMask}
                width={screen.width}
                height={screen.height}
                alt=""
                className="pointer-events-none absolute inset-0 h-full w-full max-w-none object-cover"
              />
            )}
          </div>
        ))}

        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 46%, rgba(18,20,18,0) 55%, rgba(15,18,16,.1) 78%, rgba(10,12,11,.34) 100%)",
            boxShadow: "inset 0 0 7cqw rgba(8,10,9,.18)",
          }}
        />
      </>
    </DeviceScreenFrame>
  );
}

const ACTIVATION_CARD_ICONS = {
  cognitive: FaBrain,
  physical: FaPersonRunning,
};

function ActivationFeatureTile({ card, index }) {
  const tilt = useTilt(2.5);
  const Icon = ACTIVATION_CARD_ICONS[card.icon] || FaBrain;

  return (
    <Reveal delay={index * 110}>
      <article
        ref={tilt.ref}
        onMouseMove={tilt.onMouseMove}
        onMouseLeave={tilt.onMouseLeave}
        className="card-tilt relative min-h-[26rem] overflow-hidden rounded-[26px] border border-[color:var(--border)] bg-[color:var(--bg)] shadow-[0_30px_72px_-48px_rgba(36,33,30,.48)] lg:min-h-[32rem]"
      >
        <img
          src={card.image}
          alt={card.imageAlt}
          loading="lazy"
          decoding="async"
          className="absolute inset-y-0 right-0 h-full w-[68%] object-cover object-center"
          style={card.imagePosition ? { objectPosition: card.imagePosition } : undefined}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, var(--bg) 0%, var(--bg) 34%, color-mix(in srgb, var(--bg) 97%, transparent) 45%, color-mix(in srgb, var(--bg) 54%, transparent) 59%, transparent 76%)",
          }}
        />

        <div className="relative z-10 flex min-h-[26rem] w-[50%] flex-col items-start justify-center p-8 sm:p-10 lg:min-h-[32rem] lg:p-12">
          <span
            aria-hidden
            className="grid h-14 w-14 place-items-center rounded-full border border-[color:var(--border)] bg-white/75 text-[color:var(--color-primary)] shadow-sm"
          >
            <Icon className="h-6 w-6" />
          </span>
          <h3 className="ink-heading mt-7 text-[clamp(1.8rem,2.1vw,2.25rem)] font-semibold leading-[1.02] tracking-[-0.035em]">
            {card.title}
          </h3>
          <p className="mt-6 max-w-[25rem] text-[clamp(1.05rem,1.25vw,1.25rem)] leading-relaxed text-[color:var(--ink-2)]">
            {card.body}
          </p>
        </div>
      </article>
    </Reveal>
  );
}

export default function ActivationSection() {
  const { ACTIVATION } = useSteffiCopy();
  const localize = useLocalizedAsset();
  const routinesShot = localize(APP_SHOTS.activationRoutines);
  const checkShot = localize(APP_SHOTS.activationCheck);
  const [stageRef, inView] = useInView({ threshold: 0.35, once: false });
  const [active, setActive] = useState(-1);
  const mock = useMockScreens();

  /* Steffi keeps offering something — she looks at it, then suggests it. */
  useEffect(() => {
    if (!inView) {
      setActive(-1);
      return undefined;
    }
    let i = 0;
    const step = () => {
      setActive(i % ORBIT.length);
      i += 1;
    };
    step();
    const id = setInterval(step, 2800);
    return () => clearInterval(id);
  }, [inView]);

  return (
    <section
      id="aktivierung"
      className="relative overflow-hidden py-28 lg:py-40"
      style={{
        background:
          "var(--bg)",
      }}
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-10">
        <div className="mx-auto max-w-[64rem] text-center">
          <Reveal as="p" className="t-eyebrow">
            {ACTIVATION.eyebrow}
          </Reveal>
          <Reveal
            as="h2"
            delay={80}
            className="t-section mt-6 !text-[clamp(3.25rem,6.5vw,6rem)] !leading-[0.96]"
          >
            {ACTIVATION.title.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </Reveal>
          <Reveal as="p" delay={160} className="t-lead mt-7 text-[color:var(--ink-2)]">
            {ACTIVATION.subtitle}
          </Reveal>
        </div>

        <div ref={stageRef} className="relative mx-auto -mt-2 w-full max-w-[68rem] lg:-mt-20">
          <div
            aria-hidden
            className="glow-blob pointer-events-none absolute left-1/2 top-1/2 h-[52%] w-[52%] -translate-x-1/2 -translate-y-1/2"
            style={{
              background: "radial-gradient(circle, rgba(184,199,177,.4) 0%, rgba(244,235,225,0) 70%)",
            }}
          />

          <div className="relative mx-auto aspect-[1/1.18] w-full">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[clamp(18rem,74vw,24.5rem)] lg:w-[clamp(25rem,63%,37rem)]">
                <SteffiDevice
                  variant="green"
                  live={false}
                  screen={<ActivityGameScreen active={inView} />}
                  alt={ACTIVATION.alts.device}
                />
              </div>
            </div>

            {/* the suggestions themselves — desktop only, they need the room */}
            <div aria-hidden className="pointer-events-none absolute inset-0 hidden lg:block">
              {ORBIT.map((item, i) => {
                const a = (item.angle * Math.PI) / 180;
                const x = 50 + Math.cos(a) * item.radius;
                const y = 50 + Math.sin(a) * item.radius * 0.97;
                const isActive = active === i;
                return (
                  <span
                    key={ACTIVATION.suggestions[i]}
                    className={`absolute inline-flex items-center gap-3 whitespace-nowrap rounded-full border px-5 py-3 text-[1.05rem] font-medium reveal ${
                      inView ? "is-in" : ""
                    }`}
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: "translate(-50%,-50%)",
                      transitionDelay: `${240 + i * 90}ms`,
                      background: isActive ? "#ffffff" : "rgba(255,255,255,.72)",
                      borderColor: isActive ? "var(--accent)" : "var(--border)",
                      color: isActive ? "var(--ink)" : "var(--ink-2)",
                      boxShadow: isActive
                        ? "0 18px 40px -22px rgba(201,165,125,.85)"
                        : "0 10px 26px -22px rgba(36,33,30,.5)",
                      backdropFilter: "blur(8px)",
                      animation: `steffi-hover ${7 + (i % 4)}s cubic-bezier(.2,.8,.2,1) ${i * 0.6}s infinite`,
                    }}
                  >
                    <item.Icon
                      className="h-[1.15rem] w-[1.15rem]"
                      style={{ color: isActive ? "var(--accent)" : "var(--ink-3)" }}
                      aria-hidden
                    />
                    {ACTIVATION.suggestions[i]}
                  </span>
                );
              })}
            </div>
          </div>

          {/* compact list for narrow screens */}
          <div className="mt-8 flex flex-wrap justify-center gap-2.5 lg:hidden">
            {ORBIT.map((item, i) => (
              <span
                key={ACTIVATION.suggestions[i]}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border)] bg-white/70 px-3.5 py-2 text-[0.875rem] text-[color:var(--ink-2)]"
              >
                <item.Icon className="h-4 w-4 text-[color:var(--ink-3)]" aria-hidden />
                {ACTIVATION.suggestions[i]}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-7 lg:-mt-20 lg:grid-cols-2">
          {ACTIVATION.cards.map((card, i) => (
            <ActivationFeatureTile key={card.title} card={card} index={i} />
          ))}
        </div>

        {/* Exercises are selected and scheduled remotely in the Family App. */}
        <Reveal className="mt-6">
          <div className="card grid grid-cols-1 items-center gap-10 overflow-hidden px-8 pt-10 sm:px-14 sm:pt-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(30rem,1.1fr)] lg:gap-14">
            <div className="max-w-[35rem] pb-4 sm:pb-8 lg:pb-14">
              <p className="t-eyebrow">{ACTIVATION.game.eyebrow}</p>
              <h3 className="t-feature mt-5 !text-[clamp(2rem,3vw,3.25rem)] !leading-[1.02]">
                {ACTIVATION.game.title}
              </h3>
              <p className="mt-6 text-[clamp(1.1rem,1.35vw,1.3rem)] leading-relaxed text-[color:var(--ink-2)]">
                {ACTIVATION.game.body}
              </p>
              <ul className="mt-7 flex flex-wrap gap-2">
                {ACTIVATION.game.points.map((point) => (
                  <li
                    key={point}
                    className="rounded-full border border-[color:var(--border)] bg-[color:var(--bg)] px-4 py-2 text-[0.9375rem] text-[color:var(--ink-2)]"
                  >
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-end justify-center gap-3 sm:gap-5 lg:justify-end">
              {mock ? (
                <PhoneMock className="phone-mock--pair" alt={ACTIVATION.alts.pick} />
              ) : (
              <img
                src={routinesShot.src}
                width={routinesShot.w}
                height={routinesShot.h}
                alt={ACTIVATION.alts.pick}
                loading="lazy"
                decoding="async"
                className="-mb-8 h-[clamp(24rem,36vw,32rem)] w-auto max-w-[47%] object-contain object-bottom"
                style={{ filter: "drop-shadow(0 30px 56px rgba(36,33,30,.2))" }}
              />
              )}
              {mock ? (
                <PhoneMock screen="check" className="phone-mock--pair" alt={ACTIVATION.alts.confirm} />
              ) : (
              <img
                src={checkShot.src}
                width={checkShot.w}
                height={checkShot.h}
                alt={ACTIVATION.alts.confirm}
                loading="lazy"
                decoding="async"
                className="-mb-8 h-[clamp(24rem,36vw,32rem)] w-auto max-w-[47%] object-contain object-bottom"
                style={{ filter: "drop-shadow(0 30px 56px rgba(36,33,30,.2))" }}
              />
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
